import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '../../../Components/TanstackTable'
import useReportTable from '../useReportTable'
import { AgentTransactionReportProps, TransactionData } from './types'
import { getTransactionOveralls } from './utils'

const AgentTransactionReportTable = (props: AgentTransactionReportProps) => {
    const { reportsData, isLoading } = props
    const { t } = useTranslation()

    const reports = useMemo(() => reportsData?.data || [], [reportsData])
    const months = useMemo(() => reportsData?.timePeriod || [], [reportsData])

    const columns = useMemo<ColumnDef<TransactionData>[]>(() => {
        const agentColumn: ColumnDef<TransactionData> = {
            id: 'agent',
            header: t('app.common.agent'),
            enableSorting: false,
            accessorFn: (row) => `${row.agent?.firstName} ${row.agent?.lastName}`,
        }

        const monthColumns: ColumnDef<TransactionData>[] = months?.map(({ month, monthName, year }) => ({
            id: `${month}/${year}`,
            header: monthName,
            enableSorting: false,
            cell: (info) => {
                const transaction = info.row.original.transactionTotals?.find((t) => t?.month === month)
                if (!transaction) return null
                return (
                    <div className="d-flex gap-2 justify-content-center">
                        {transaction.soldCount != null && <span>{transaction.soldCount}</span>}
                        {transaction.rentedCount != null && <span className="text-danger">{transaction.rentedCount}</span>}
                    </div>
                )
            },
        }))

        const totalColumn: ColumnDef<TransactionData> = {
            id: 'total_by_agent',
            header: t('app.common.total'),
            enableSorting: false,
            cell: (info) => <div className="fw-bolder text-center">{info.row.original.agentGrandTotal}</div>,
        }

        return [agentColumn, ...monthColumns, totalColumn]
    }, [months, t])

    const tableFooter = useMemo(() => {
        const { monthOveralls, grandOverall } = getTransactionOveralls(reports, months)

        return (
            <tr>
                <td>{t('app.common.overall')}</td>
                {monthOveralls.map((overall, i) => (
                    <td key={i} className="text-center">
                        {overall.count}
                    </td>
                ))}
                <td className="fw-bolder text-center">{grandOverall}</td>
            </tr>
        )
    }, [reports, months, t])

    const table = useReportTable(columns, reports)

    return (
        <DataTable
            table={table}
            tableFooter={tableFooter}
            isLoading={isLoading}
            theadClass="dmn-table-header"
        />
    )
}

export default AgentTransactionReportTable
